import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from '@mui/icons-material/Close';
import { get, post } from "app/pages/Docgen/common/network";
import MuiTableWrapper from "../mui-table-wrapper/MuiTableWrapper";
import FallbackGeneratedFormMessage from "../fallback-form-message/FallbackGeneratedFormMessage";
import { toast } from "react-toastify";
import debounce from 'lodash/debounce';
import FormSearchFilter from "../form-search-filter/FormSearchFilter";

const AddNewCategory = ({
    openModal,
    onClose,
    formType = "Category",
    callback = () => { },
    categoryId,
    fetchedCategoryName = "",
    categories = [],
}) => {
    const [forms, setForms] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [categoryName, setCategoryName] = useState(fetchedCategoryName);
    const [isFetching, setIsFetching] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [rowsSelection, setRowsSelection] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [filterText, setFilterText] = useState("");
    const [formTypeValue, setFormTypeValue] = useState("");
    const [dateValue, setDateValue] = useState("");

    const validateName = useMemo(() => debounce((name, categoriesList) => {
        const exists = categoriesList.some(
            (category) => category?.name?.trim().toLowerCase() === name.trim().toLowerCase()
        );
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (exists) {
                newErrors.categoryName = 'Category Name must be unique';
            } else {
                delete newErrors.categoryName;
            }
            return newErrors;
        });

    }, 300), []);

    const fetchForms = async () => {
        setIsFetching(true);
        try {
            let response = await get(`docgen/forms`, {}, false);
            if (response?.status === 200) {
                let data = response?.data?.data || [];
                if (formType === "Forms")
                    data = (response?.data?.data || []).filter(item => {
                        const maps = item.categoryFormMap;
                        // If it's not an array, include the item because the form is not a part of any category
                        if (!Array.isArray(maps)) return true;
                        const check = !maps.some(map => map.categoryId == parseInt(categoryId));
                        return check
                    });
                setForms(data);
            }
        } catch (error) {
        } finally {
            setIsFetching(false);
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            let formIds = selectedRows.map((row) => row.id);
            let response, message = "";
            if (formType === "Category") {
                if (!categoryName || (!Array.isArray(selectedRows) && !selectedRows.length > 0)) {
                    toast.error("Please fill the required details");
                    return;
                }
                response = await post(`/docgen/categories/`, { name: categoryName, formIds }, {}, false);
            } else {
                if (!categoryId) {
                    toast.error("Category ID not found")
                    return;
                }
                response = await post(`/docgen/categories/${categoryId}/forms`, { formIds }, {}, false);
            }
            if (response.status === 200) {
                closeModal();
                callback();
            }
        }
        catch (error) {
            if ("category name must be unique" === error?.response?.data?.message?.toLowerCase()) {
                setErrors({
                    categoryName: "Category name must be unique"
                })
            }
        }
        finally {
            setIsLoading(false);
        }
    }
    const closeModal = () => {
        if (formType === "Category")
            setCategoryName("");
        setSearchText("");
        setSelectedRows([]);
        setRowsSelection([]);
        setErrors({});
        onClose();
    }

    useEffect(() => {
        if (openModal)
            fetchForms();
    }, [openModal]);

    const handleNameChange = (e) => {
        const name = e.target.value;
        setCategoryName(name);
        validateName(name, categories);

    };

    const handleSearch = (value) => {
        const columnName = value?.value || ""; // Name of the column
        const searchValue = value?.searchedText || "";
        const dropdownValue = value?.dropdownValue || ""; // value selected in the dropdown field
        setSearchText(searchValue);
        setFilterText(columnName);
        setFormTypeValue(dropdownValue);

        if (columnName === "updated_at") {
            setDateValue(value);
        }
    };

    const columns = [
        {
            field: "form_name",
            header: "Form Name",
        },
        {
            field: "form_code",
            header: "Form Code"
        },
        {
            field: "form_type",
            header: "Type"
        },
        {
            field: "country",
            header: "Country"
        },
    ];

    const FormTypeMapping = {
        "Category": "Category",
        "Forms": "Forms"
    }

    const isEligible = categoryName && Array.isArray(selectedRows) && selectedRows.length > 0 && (Object.keys(errors) || []).length === 0
    return (
        <Modal open={openModal} onClose={closeModal} aria-labelledby="add-new-category">
            <Box sx={{
                position: 'relative',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: "80%",
                height: "85%",
                bgcolor: 'white',
                borderRadius: "0.3rem",
                p: 4,
                overflowY: "auto",
            }}>

                <IconButton
                    onClick={closeModal}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Typography id="modal-modal-title" variant="h1" component="h1">
                    Add {FormTypeMapping[formType]}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "1.25rem",
                    }}
                >
                    <div>
                        {formType === "Category" ? (
                            <TextField
                                fullWidth
                                error={Boolean(errors?.categoryName)}
                                helperText={errors?.categoryName}
                                label="Category Name"
                                id="category-name"
                                variant="outlined"
                                size="small"
                                value={categoryName}
                                onChange={handleNameChange}
                                sx={{ display: "block" }}
                                FormHelperTextProps={{
                                    sx: {
                                        display: "block",
                                        margin: 0,
                                    },
                                }}
                            />
                        ) : (
                            <h5 className="m-0">{categoryName}</h5>
                        )}
                        <small
                            style={{
                                color: "#8B8B8B",
                                display: "block",
                                marginBottom: "1rem",
                            }}
                        >
                            Select Forms to add into the category
                        </small>
                    </div>

                    <div>
                        <FormSearchFilter
                            options={[
                                { value: "", label: "All" },
                                { value: "form_name", label: "Form Name" },
                                { value: "form_code", label: "Form Code" },
                                { value: "form_type", label: "Type" },
                                { value: "country", label: "Country" },
                            ]}
                            defaultOption={{ value: "", label: "Select an option" }}
                            dateFiltersOptionsValueArray={[]}
                            dropdownArray={["form_type"]}
                            dropdownOptions={{
                                form_type: [
                                    { value: "standard", label: "Standard" },
                                    { value: "custom", label: "Custom" },
                                ],
                            }}
                            onSearch={(values) => handleSearch(values)}
                        />
                    </div>
                </Box>
                <MuiTableWrapper
                    columns={columns}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    emptyBody={<FallbackGeneratedFormMessage />}
                    colgroup={[
                        { width: "70%" },
                        { width: "14%" },
                        { width: "8%" },
                        { width: "8%" },
                    ]}
                    isFetching={isFetching}
                    data={forms}
                    searchValue={searchText}
                    filterColumn={filterText}
                    formTypeValue={formTypeValue}
                    rowsSelection={rowsSelection}
                    setRowsSelection={setRowsSelection}
                    pageSize={forms?.length || 200}
                    tableContainerStyle={{
                        maxHeight: "55vh",
                        overflowY: "auto",
                    }}
                    dateValue={dateValue}
                    tablePaginationClasses={"d-none"}
                />
                <Box sx={{ display: "flex", justifyContent: "end", marginTop: "1.5rem" }}>
                    <Button
                        variant="outlined"
                        onClick={closeModal}
                        sx={{
                            marginRight: "1rem"
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isEligible || isLoading}
                        sx={{
                            '&.Mui-disabled': {
                                pointerEvents: 'auto',
                                cursor: 'not-allowed',
                            },
                        }}
                    >
                        {
                            isLoading
                                ?
                                <CircularProgress size={"1rem"} />
                                :
                                "Save"
                        }
                    </Button>

                </Box>
            </Box >
        </Modal>

    )
}

export default AddNewCategory;